import { Box } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import style from '../jobs.modules.scss'
import { Table } from 'components/core-ui/Table'
import { useNavigate } from 'react-router-dom'
import { useJobCategoriesQuery } from 'components/iam/jobs/api'
import { rowActions } from '../helpers'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import EditJobCategoryModal from './EditJobCategory/EditJobCategoryModal'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { toolbarActions, columns } from './helpers'

const JobCategories: React.FC = () => {
    const [modalState, setModalState] = useState({
        isEditModalOpen: false,
        isDisableModalOpen: false,
        selectedRowId: null
    });
    const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false);
    const { canCreate, canEdit, canDelete } = { canCreate: true, canEdit: true, canDelete: false };
    
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const openEditModal = useCallback((jobId?: number) => {
        setModalState({ ...modalState, isEditModalOpen: true, selectedRowId: jobId });
    }, [modalState]);

    const closeEditModal = useCallback(() => {
        setModalState({ ...modalState, isEditModalOpen: false, selectedRowId: null });
        refetch();
    }, [modalState]);

    const openDisableModal = useCallback((jobId: number) => {
        setModalState({ ...modalState, isDisableModalOpen: true, selectedRowId: jobId });
    }, [modalState]);

    const closeDisableModal = useCallback(() => {
        setModalState({ ...modalState, isDisableModalOpen: false, selectedRowId: null });
        refetch();
    }, [modalState]);

    const selectRow = useCallback((rowId: number, action: string) => {
        action === 'edit' ? openEditModal(rowId) : openDisableModal(rowId);
    }, [openEditModal, openDisableModal]);

    const [fetchParams, setFetchParams] = useState({
        page: 0,
        rowsPerPage: 10,
        order: 'asc',
        orderBy: 'name',
        tableFilters: [],
        searchText: ''
    })

    const { data: jobsCategoryData, isLoading, refetch } = useJobCategoriesQuery(showNotification, toTableGETUrl({
        ...fetchParams,
        searchFields: ['name'],
        searchText: fetchParams.searchText,
        url: '/iam/management-tools/job-categories'
    }))

    const handleRemoteData = useCallback(({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
        setFetchParams({ page, rowsPerPage, order, orderBy, tableFilters, searchText })
    }, [])

    

    const toolbar = useMemo(() => toolbarActions({ canCreate, navigate, openModal: openEditModal}), [canCreate, navigate, openEditModal])

    return (
        <div className={style.mainContainer}>
            {canEdit &&  (
                <EditJobCategoryModal
                    isModalOpen={modalState.isEditModalOpen}
                    onModalClose={closeEditModal}
                    jobCatId={modalState.selectedRowId}
                    onSuccess={refetch}
                />
            )}
            {canDelete && (
                <DeleteConfirmationModal
                    isModalOpen={modalState.isDisableModalOpen}
                    actionUrl='/iam/management-tools/jobs/:id'
                    content='Are you sure you want to delete this job category?'
                    closeModal={closeDisableModal}
                    itemId={modalState.selectedRowId}
                    setShouldRefreshData={setShouldRefreshData}
                />
            )}
            <Box>
                <Table
                    hideCheckbox
                    hasSearchInput
                    orderBy={fetchParams.orderBy}
                    order={fetchParams.order}
                    columns={columns}
                    items={jobsCategoryData?.list || []}
                    isLoading={isLoading}
                    totalCount={jobsCategoryData?.page.totalCount || fetchParams.page}
                    shouldRefreshData={shouldRefreshData}
                    setShouldRefreshData={setShouldRefreshData}
                    rowsPerPage={fetchParams.rowsPerPage}
                    rowActionsWidth="130px"
                    fetchData={handleRemoteData}
                    rowActions={rowActions({ canEdit, canDelete, navigate, selectRow, detailsUrl: '/iam/jobManagement/jobCategory' })}
                    toolbarActions={toolbar}
                />
            </Box>
        </div>
    );
};

export default JobCategories;